import { HttpStatus } from '@/shared/constants/http-status'

/**
 * El `fetch` con el que viaja TODA llamada autenticada a la API v2: si el access token venció
 * a mitad de camino, lo renueva y reintenta la request original, sin que el usuario se entere.
 *
 * **Por qué existe.** El access token v2 dura 15 minutos y vive sólo en memoria; la cookie de
 * refresh dura 30 días. La web tenía dos caminos de red y sólo uno sabía curarse: `axiosPrivate`
 * (API v1) tiene un interceptor 401 → refresh → retry, pero el SDK generado de la v2 recibe el
 * Bearer como ARGUMENTO explícito (`bearer(accessToken)`) en cada uno de sus servicios. Un
 * argumento es una fotocopia: cuando vence, nadie del lado del que llama puede renovarla. Por eso
 * dejar la pestaña abierta 15 minutos y volver rompía todo listado, y el botón «Reintentar»
 * —que es un `refetch()` sobre la misma `queryFn`— reenviaba exactamente el mismo token muerto.
 * Sólo recargar la página curaba, porque `PersistLogin` refresca al montar.
 *
 * **Dónde se engancha y por qué acá.** El punto natural parecería ser un interceptor de respuesta
 * del cliente hey-api, pero para ese momento el body del `Request` ya se consumió y no hay nada
 * que reintentar. Envolviendo el `fetch` clonamos ANTES de enviar, que es la única ventana en la
 * que el reintento es posible.
 *
 * **Reintentar cualquier método es seguro, incluido POST.** Un 401 lo devuelve el guard de
 * autenticación, antes de que el handler toque nada: la request no llegó a ejecutarse del lado
 * del servidor, así que no hay efecto que duplicar.
 */

type SessionRecovery = {
  /**
   * Renueva la sesión y devuelve el access token nuevo, o `null` si no se pudo.
   * Se espera que DEDUPLIQUE: ocho listados que fallan juntos —el caso real al volver a una
   * pestaña vieja— tienen que producir un solo refresh contra la API, no ocho.
   */
  refresh: () => Promise<string | null>
  /** La sesión murió de verdad (cookie vencida o revocada) y hay que volver al login. */
  onSessionLost: () => void
}

/**
 * Registro mutable a nivel de módulo, y no un parámetro, porque el dueño del refresh es
 * `AuthProvider` —un componente— y quien necesita usarlo es el transporte, que no vive en el
 * árbol de React. `AuthProvider` se registra al montar y se da de baja al desmontar.
 */
let recovery: SessionRecovery | null = null

export function registerV2SessionRecovery(next: SessionRecovery | null): void {
  recovery = next
}

/** Cómo se pone una request en el cable. La firma de `fetch`, para poder cambiarla. */
export type V2Transport = (request: Request) => Promise<Response>

const defaultTransport: V2Transport = (request) => globalThis.fetch(request)

/**
 * La renovación de sesión, sobre el transporte que se le indique.
 *
 * ⭐ El transporte es un parámetro y no `globalThis.fetch` a secas por UN caso concreto: la subida
 * de una imagen del editor necesita informar cuánto lleva subido, y `fetch` no lo dice —hay que
 * mandarla por `XMLHttpRequest`—. Sin este parámetro, esa llamada tendría que elegir entre la barra
 * de progreso y la renovación de sesión: un token vencido a los quince minutos dejaría la subida en
 * un 401 seco justo cuando el autor volvió a la pestaña. Con él no hay que elegir.
 */
export const createFetchWithSessionRecovery =
  (transport: V2Transport) =>
  async (input: URL | RequestInfo, init?: RequestInit): Promise<Response> =>
    await recoverSession(transport, input, init)

/**
 * Respeta la firma completa de `fetch` porque es la que exige la config del SDK, aunque en la
 * práctica hey-api siempre nos entregue un `Request` ya armado (arma la URL y las cabeceras
 * antes de llamarnos). Normalizamos para no depender de eso.
 */
export async function fetchWithSessionRecovery(
  input: URL | RequestInfo,
  init?: RequestInit
): Promise<Response> {
  return await recoverSession(defaultTransport, input, init)
}

async function recoverSession(
  transport: V2Transport,
  input: URL | RequestInfo,
  init?: RequestInit
): Promise<Response> {
  const request = input instanceof Request && init === undefined ? input : new Request(input, init)

  // El clon se saca ANTES del envío: `fetch` consume el stream del body del original.
  const retryable = request.clone()
  const response = await transport(request)

  if (response.status !== HttpStatus.UNAUTHORIZED) return response

  const session = recovery
  // Sin `AuthProvider` montado (login, recuperación de contraseña) no hay sesión que renovar:
  // el 401 es la respuesta legítima y viaja tal cual.
  if (!session) return response

  let accessToken: string | null = null
  try {
    accessToken = await session.refresh()
  } catch {
    accessToken = null
  }

  if (!accessToken) {
    session.onSessionLost()
    return response
  }

  const headers = new Headers(retryable.headers)
  headers.set('Authorization', `Bearer ${accessToken}`)
  return await transport(new Request(retryable, { headers }))
}
